import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Clock,
  RectangleHorizontal,
  RectangleVertical,
  Square,
  Captions as CaptionsIcon,
  Coins,
  Globe2,
  Loader2,
  MessageSquare,
  AlertCircle,
  ImageIcon,
  Film,
  Music2,
  X,
  ZoomIn,
} from 'lucide-react';

import { Button, Textarea } from '@/components/ui';
import { apiService } from '@/api';
import { cn } from '@/utils/cn';
import { calculateHeygenCredits } from '@/utils/constants';

/** ===== Types ===== */

export type PlanSceneType = 'a_roll' | 'b_roll' | string;

export interface PlanScene {
  title?: string;
  start?: number;
  end?: number;
  type?: PlanSceneType;
  vo_script?: string;
  visual_explanation?: string;
  /** LongVid only: per-speaker lines (speaker is "narrator" or a character name). */
  speech?: { speaker: string; text: string }[];
}

export interface Plan {
  title?: string;
  project_header_photo_resource_id?: string | null;
  concept_explanation?: string;
  style_explanation?: string;
  avatar_id?: string | null;
  voice_id?: string | null;
  duration_seconds?: number;
  orientation?: 'landscape' | 'portrait' | 'square' | string;
  language?: string;
  cc_enabled?: boolean;
  scenes?: PlanScene[];
  /** LongVid only: attached files and, in the customer's words, what each is used for. */
  files?: { name: string; use: string }[];
  /** The length the customer asked for, when it is past the free cap. */
  requested_duration_sec?: number;
}

/**
 * Finds the first fenced ```json block in the agent's message and parses it.
 * Returns null if no valid JSON plan is found. Tolerant of:
 *   - plain ``` fences (without `json` language tag)
 *   - leading prose before the fence
 *   - trailing prose after the fence
 *   - bare JSON (no fence) as a last resort
 */
export function extractPlanJson(text: string): Plan | null {
  if (!text || typeof text !== 'string') return null;

  const fenceMatch = text.match(/```(?:json)?\s*\n([\s\S]*?)\n```/i);
  if (fenceMatch && fenceMatch[1]) {
    try {
      const parsed = JSON.parse(fenceMatch[1]);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Plan;
      }
    } catch {
      // fall through to bare-JSON attempt
    }
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const candidate = text.slice(firstBrace, lastBrace + 1);
    try {
      const parsed = JSON.parse(candidate);
      if (
        parsed
        && typeof parsed === 'object'
        && !Array.isArray(parsed)
        && ('scenes' in parsed || 'title' in parsed || 'concept_explanation' in parsed)
      ) {
        return parsed as Plan;
      }
    } catch {
      // not JSON
    }
  }

  return null;
}

/** ===== Helpers ===== */

function normalizeOrientation(o?: string): 'landscape' | 'portrait' | 'square' | undefined {
  if (!o) return undefined;
  const t = o.toLowerCase();
  if (t.includes('portrait') || t.includes('vertical') || t.includes('9:16')) return 'portrait';
  if (t.includes('square') || t.includes('1:1')) return 'square';
  if (t.includes('landscape') || t.includes('horizontal') || t.includes('16:9')) return 'landscape';
  return undefined;
}

function aspectIcon(aspect?: string) {
  const a = normalizeOrientation(aspect);
  if (a === 'portrait') return RectangleVertical;
  if (a === 'square') return Square;
  return RectangleHorizontal;
}

function sceneTimeRange(scene: PlanScene): string | undefined {
  if (typeof scene.start === 'number' && typeof scene.end === 'number') {
    return `${scene.start}-${scene.end}s`;
  }
  if (typeof scene.end === 'number') return `~${scene.end}s`;
  return undefined;
}

/** ===== Resource image loader ===== */

interface ResourceImageProps {
  userId: string;
  orderId: string;
  resourceId: string;
  cache: Map<string, Record<string, unknown>>;
  alt?: string;
  className?: string;
  /**
   * Render an icon-sized letter fallback when the resource couldn't be
   * resolved or the response didn't contain a displayable image. Defaults to
   * a gradient + video glyph so the hero still looks intentional.
   */
  fallback?: React.ReactNode;
}

/**
 * The backend unwraps `data` from HeyGen's resource response, so the object
 * we receive looks like:
 *   { resource_id, resource_type, url, thumbnail_url, preview_url, metadata: {...} }
 *
 * For voices, `url` is the audio sample itself. For avatars, `url` / `preview_url`
 * are the preview image. Names and extra fields live on `metadata`.
 */

function resourceType(res: Record<string, unknown> | undefined): string | undefined {
  if (!res) return undefined;
  const t = res.resource_type;
  return typeof t === 'string' ? t.toLowerCase() : undefined;
}

function metadataOf(res: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!res) return undefined;
  const m = res.metadata;
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : undefined;
}

function pickImageUrl(res: Record<string, unknown> | undefined): string | undefined {
  if (!res) return undefined;
  const type = resourceType(res);
  // For voices, the top-level `url` is audio — skip it.
  const keys = type === 'voice'
    ? ['thumbnail_url', 'preview_image_url', 'image_url', 'poster_url', 'avatar_url']
    : ['thumbnail_url', 'preview_url', 'url', 'image_url', 'preview_image_url', 'poster_url', 'avatar_url'];
  for (const key of keys) {
    const v = res[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  const meta = metadataOf(res);
  if (meta) {
    for (const key of keys) {
      const v = meta[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return undefined;
}

function pickResourceName(res: Record<string, unknown> | undefined): string | undefined {
  if (!res) return undefined;
  const keys = ['display_name', 'name', 'title', 'label'];
  for (const key of keys) {
    const v = res[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  const meta = metadataOf(res);
  if (meta) {
    for (const key of keys) {
      const v = meta[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return undefined;
}

export function ResourceImage({
  userId,
  orderId,
  resourceId,
  cache,
  alt,
  className,
  fallback,
}: ResourceImageProps) {
  const [resource, setResource] = useState<Record<string, unknown> | null>(() => cache.get(resourceId) || null);
  const [loading, setLoading] = useState(!resource);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(resourceId);
    if (cached) {
      setResource(cached);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    apiService.getHeygenChatResource(userId, orderId, resourceId)
      .then((res) => {
        if (cancelled) return;
        cache.set(resourceId, res);
        setResource(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderId, resourceId]);

  const url = pickImageUrl(resource || undefined);

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center bg-surface-light/50', className)}>
        <Loader2 className="w-5 h-5 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (!url) {
    return <div className={cn('flex items-center justify-center', className)}>{fallback}</div>;
  }

  return (
    <img
      src={url}
      alt={alt || resourceId}
      className={cn('object-cover', className)}
      loading="lazy"
    />
  );
}

/** ===== Image lightbox ===== */

/**
 * Simple image-only lightbox. Fills the viewport with a darkened backdrop,
 * shows the image centered at up to 80vw/80vh, and closes on backdrop click,
 * X button click, or Escape key.
 */
function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-surface/80 hover:bg-surface text-text-primary border border-glass-border transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[80vw] max-h-[80vh] w-auto h-auto rounded-xl shadow-2xl object-contain animate-slide-up"
      />
    </div>
  );
}

/** ===== Avatar + voice cards ===== */

interface AvatarChipProps {
  userId: string;
  orderId: string;
  resourceId: string;
  fallbackName?: string;
  cache: Map<string, Record<string, unknown>>;
  /**
   * `chip` (default): small square tile + name next to it. Used anywhere we
   *   need a compact avatar reference.
   * `portrait`: big square portrait with no name label; the name is only
   *   used as alt/aria text. This is the hero-column avatar in the plan
   *   card's 2-column header.
   */
  variant?: 'chip' | 'portrait';
}

function AvatarChip({
  userId,
  orderId,
  resourceId,
  fallbackName,
  cache,
  variant = 'chip',
}: AvatarChipProps) {
  const [resource, setResource] = useState<Record<string, unknown> | null>(() => cache.get(resourceId) || null);
  const [loading, setLoading] = useState(!resource);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(resourceId);
    if (cached) {
      setResource(cached);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    apiService.getHeygenChatResource(userId, orderId, resourceId)
      .then((res) => {
        if (cancelled) return;
        cache.set(resourceId, res);
        setResource(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderId, resourceId]);

  const resolvedName = pickResourceName(resource || undefined);
  const name = resolvedName || fallbackName || resourceId;
  const imageUrl = pickImageUrl(resource || undefined);
  const initial = name.replace(/[^A-Za-z0-9]/g, '').slice(0, 1).toUpperCase() || '?';
  const canZoom = !!imageUrl;

  if (variant === 'portrait') {
    return (
      <>
        <button
          type="button"
          disabled={!canZoom}
          onClick={() => canZoom && setZoomed(true)}
          className={cn(
            'group relative w-full aspect-square rounded-2xl overflow-hidden',
            'bg-gradient-to-br from-primary/35 to-purple-700/35 border border-glass-border',
            'flex items-center justify-center text-white text-5xl font-semibold',
            canZoom
              ? 'cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/60'
              : 'cursor-default',
          )}
          aria-label={canZoom ? `Zoom avatar ${name}` : name}
        >
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : imageUrl ? (
            <>
              <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                <ZoomIn className="w-6 h-6 text-white" />
              </span>
            </>
          ) : (
            <span>{initial}</span>
          )}
        </button>
        {zoomed && imageUrl && (
          <ImageLightbox src={imageUrl} alt={name} onClose={() => setZoomed(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <div className="rounded-xl bg-surface-light/50 border border-glass-border px-3 py-2.5 flex items-center gap-3">
        <button
          type="button"
          disabled={!canZoom}
          onClick={() => canZoom && setZoomed(true)}
          className={cn(
            'group relative w-10 h-10 rounded-lg overflow-hidden',
            'bg-gradient-to-br from-primary/40 to-purple-700/40',
            'flex items-center justify-center text-text-primary text-base font-semibold flex-shrink-0',
            canZoom
              ? 'cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/60'
              : 'cursor-default',
          )}
          aria-label={canZoom ? `Zoom avatar ${name}` : name}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : imageUrl ? (
            <>
              <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4 text-white" />
              </span>
            </>
          ) : (
            <span>{initial}</span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-text-primary truncate">
            {loading && !resolvedName ? '…' : name}
          </div>
        </div>
      </div>
      {zoomed && imageUrl && (
        <ImageLightbox src={imageUrl} alt={name} onClose={() => setZoomed(false)} />
      )}
    </>
  );
}

/** ===== Inline scene resource chip (hover-preview) ===== */

/**
 * Parses text like: "A hand interacting with [[laptop_pointing_brxyz]] on a laptop."
 * into a sequence of text / chip segments. As a resilience fallback we also match
 * bare resource-shaped IDs that the agent may have inlined without brackets —
 * e.g. `image_xyz123`, `thumbnail_image_…`, `video_…`, `audio_…`, `sound_…`,
 * `broll_…`, `photo_…`.
 */
type VisualSegment =
  | { kind: 'text'; text: string }
  | { kind: 'chip'; resourceId: string; displayName?: string };

const BARE_RESOURCE_PREFIXES = [
  'thumbnail_image',
  'image',
  'photo',
  'video',
  'broll',
  'b_roll',
  'a_roll',
  'footage',
  'sound',
  'audio',
  'music',
  'sfx',
];

// [[<id>]] or [[<id>|<display name>]]
const BRACKET_RE = /\[\[([A-Za-z0-9_\-]+)(?:\s*\|\s*([^\]]+))?\]\]/g;
// Bare ids that look like `<known_prefix>_<alphanumeric>` with >=4 chars of id body.
const BARE_RE = new RegExp(
  `\\b(${BARE_RESOURCE_PREFIXES.join('|')})_[a-z0-9]{4,}\\b`,
  'gi',
);

function parseVisualExplanation(text: string): VisualSegment[] {
  if (!text) return [];

  // Collect non-overlapping matches from both regexes, sorted by start index.
  type RawMatch = { start: number; end: number; resourceId: string; displayName?: string };
  const matches: RawMatch[] = [];

  for (const m of text.matchAll(BRACKET_RE)) {
    if (m.index === undefined) continue;
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      resourceId: m[1],
      displayName: m[2]?.trim() || undefined,
    });
  }

  // Track bracketed ranges so we don't double-match the bare id inside them.
  const bracketRanges = matches.map(m => [m.start, m.end] as const);
  for (const m of text.matchAll(BARE_RE)) {
    if (m.index === undefined) continue;
    const overlaps = bracketRanges.some(([s, e]) => m.index! >= s && m.index! < e);
    if (overlaps) continue;
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      resourceId: m[0],
    });
  }

  matches.sort((a, b) => a.start - b.start);

  const segments: VisualSegment[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      segments.push({ kind: 'text', text: text.slice(cursor, m.start) });
    }
    segments.push({ kind: 'chip', resourceId: m.resourceId, displayName: m.displayName });
    cursor = m.end;
  }
  if (cursor < text.length) {
    segments.push({ kind: 'text', text: text.slice(cursor) });
  }

  return segments;
}

function humanizeResourceId(id: string): string {
  // Strip HeyGen's trailing random suffix (last `_<5-10 alphanumeric>` tail)
  // and turn underscores into spaces. "laptop_pointing_brxyz" → "Laptop pointing".
  const stripped = id.replace(/_[a-z0-9]{5,10}$/i, '').replace(/^thumbnail_image_?/i, '');
  const clean = stripped.replace(/[_-]+/g, ' ').trim();
  if (!clean) return id;
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function resourceIcon(res: Record<string, unknown> | undefined) {
  const type = resourceType(res);
  if (type === 'voice' || type === 'audio' || type === 'sound' || type === 'music') return Music2;
  if (type === 'video' || type === 'draft' || type === 'broll' || type === 'footage') return Film;
  return ImageIcon;
}

interface SceneResourceChipProps {
  userId: string;
  orderId: string;
  resourceId: string;
  fallbackLabel?: string;
  cache: Map<string, Record<string, unknown>>;
}

function SceneResourceChip({ userId, orderId, resourceId, fallbackLabel, cache }: SceneResourceChipProps) {
  const [resource, setResource] = useState<Record<string, unknown> | null>(() => cache.get(resourceId) || null);
  const [loading, setLoading] = useState(!resource);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(resourceId);
    if (cached) {
      setResource(cached);
      setLoading(false);
      return () => { cancelled = true; };
    }
    // Lazy-fetch only when the user hovers (avoids 10+ parallel calls on
    // initial render of a long plan). But we still let the label resolve
    // opportunistically by starting the fetch here.
    setLoading(true);
    apiService.getHeygenChatResource(userId, orderId, resourceId)
      .then((res) => {
        if (cancelled) return;
        cache.set(resourceId, res);
        setResource(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderId, resourceId]);

  const label = fallbackLabel
    || pickResourceName(resource || undefined)
    || humanizeResourceId(resourceId);

  const previewUrl = pickImageUrl(resource || undefined);
  const Icon = resourceIcon(resource || undefined);

  return (
    <span
      className="relative inline-block align-baseline"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <span
        tabIndex={0}
        className={cn(
          'inline-flex items-center gap-1 align-baseline rounded-md',
          'px-1.5 py-0.5 mx-0.5',
          'bg-primary/15 text-primary border border-primary/25',
          'text-[0.85em] font-medium leading-tight cursor-default',
          'transition-colors hover:bg-primary/25'
        )}
        title={label}
      >
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className="truncate max-w-[220px]">{label}</span>
      </span>

      {hovered && previewUrl && (
        <span
          className={cn(
            'pointer-events-none absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2',
            'rounded-lg overflow-hidden shadow-xl border border-glass-border bg-surface',
            'w-44 h-28 flex items-center justify-center',
          )}
        >
          <img
            src={previewUrl}
            alt={label}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </span>
      )}

      {hovered && !previewUrl && loading && (
        <span
          className={cn(
            'pointer-events-none absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2',
            'rounded-lg overflow-hidden shadow-xl border border-glass-border bg-surface',
            'w-44 h-28 flex items-center justify-center',
          )}
        >
          <Loader2 className="w-4 h-4 animate-spin text-text-tertiary" />
        </span>
      )}
    </span>
  );
}

/**
 * Renders scene `visual_explanation` text with inline resource chips for any
 * [[resource_id]] markers and recognized bare resource IDs.
 */
function VisualExplanation({
  text,
  userId,
  orderId,
  cache,
}: {
  text: string;
  userId: string;
  orderId: string;
  cache: Map<string, Record<string, unknown>>;
}) {
  const segments = parseVisualExplanation(text);
  if (segments.length === 0) return null;
  return (
    <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
      {segments.map((seg, i) => {
        if (seg.kind === 'text') return <span key={i}>{seg.text}</span>;
        return (
          <SceneResourceChip
            key={`chip-${i}-${seg.resourceId}`}
            userId={userId}
            orderId={orderId}
            resourceId={seg.resourceId}
            fallbackLabel={seg.displayName}
            cache={cache}
          />
        );
      })}
    </p>
  );
}

/** ===== Chips ===== */

interface ChipProps {
  icon?: typeof Clock;
  children: React.ReactNode;
  tooltip?: string;
}

function Chip({ icon: Icon, children, tooltip }: ChipProps) {
  return (
    <div
      title={tooltip}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-light/70 border border-glass-border text-xs text-text-primary whitespace-nowrap"
    >
      {Icon ? <Icon className="w-3.5 h-3.5 text-text-secondary" /> : null}
      <span>{children}</span>
    </div>
  );
}

/** ===== Main component ===== */

export interface PlanCardProps {
  plan: Plan;
  userId: string;
  orderId: string;
  /**
   * Shared resource cache so re-renders / sibling cards don't refetch
   * already-resolved HeyGen resource IDs.
   */
  resourceCache: Map<string, Record<string, unknown>>;
  userCredits?: number;
  onApprove?: (durationSec: number) => Promise<void> | void;
  onRequestChanges?: (message: string) => Promise<void> | void;
  isApproving?: boolean;
  isRevising?: boolean;
  disabled?: boolean;
  className?: string;
}

export function PlanCard({
  plan,
  userId,
  orderId,
  resourceCache,
  userCredits,
  onApprove,
  onRequestChanges,
  isApproving,
  isRevising,
  disabled,
  className,
}: PlanCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showRevise, setShowRevise] = useState(false);
  const [reviseText, setReviseText] = useState('');

  /**
   * User ran out of credits while looking at the plan — send them to the
   * subscription/pricing page so they can top up and come back. We leave the
   * modal open on purpose: when they navigate back the plan is still there,
   * and if they refresh they can resume via /files -> ?plan=<orderId>.
   */
  const handleGetCreditsClick = () => {
    // No inline paywall_shown here — the pricing page fires the impression on
    // arrival (with this context), so the encounter isn't counted twice.
    navigate('/subscription?context=insufficient_credits');
  };

  const orientation = normalizeOrientation(plan.orientation);
  const AspectIcon = aspectIcon(plan.orientation);
  const durationSec = typeof plan.duration_seconds === 'number' && plan.duration_seconds > 0
    ? plan.duration_seconds
    : undefined;

  const credits = durationSec
    ? calculateHeygenCredits(durationSec)
    : undefined;

  const insufficientCredits = typeof userCredits === 'number'
    && typeof credits === 'number'
    && userCredits < credits;

  const avatarResourceId = plan.avatar_id || undefined;

  const scenes = Array.isArray(plan.scenes) ? plan.scenes : [];

  const handleGenerateClick = async () => {
    if (!onApprove || !durationSec || insufficientCredits || isApproving) return;
    await onApprove(durationSec);
  };

  const handleSubmitRevision = async () => {
    const trimmed = reviseText.trim();
    if (!trimmed || !onRequestChanges || isRevising) return;
    await onRequestChanges(trimmed);
    setReviseText('');
    setShowRevise(false);
  };

  const aspectLabel = orientation
    ? t(`plan.card.orientations.${orientation}` as const)
    : plan.orientation;

  return (
    <div className={cn('w-full', className)}>
      <div className="rounded-3xl overflow-hidden bg-surface border border-glass-border">
        <div className="p-5 md:p-6 space-y-6">
          {/*
            Top hero: 2-column layout on >= md. Left column is the avatar
            portrait (no name label — identity comes from the face).
            Right column is title + script + style stacked vertically.
            On mobile we collapse to a single column with the avatar on top.
          */}
          <div
            className={cn(
              'grid gap-5 md:gap-6',
              avatarResourceId
                ? 'grid-cols-1 md:grid-cols-[minmax(0,240px)_1fr]'
                : 'grid-cols-1',
            )}
          >
            {avatarResourceId && (
              <div className="w-full max-w-[240px] mx-auto md:mx-0 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary text-center md:text-left">
                  {t('plan.card.narrator')}
                </div>
                <AvatarChip
                  userId={userId}
                  orderId={orderId}
                  resourceId={avatarResourceId}
                  cache={resourceCache}
                  variant="portrait"
                />
              </div>
            )}

            <div className="space-y-5">
              {plan.title && (
                <h2 className="text-xl md:text-2xl font-bold text-text-primary leading-tight">
                  {plan.title}
                </h2>
              )}

              {plan.concept_explanation && (
                <div>
                  <div className="text-sm font-semibold text-text-primary mb-1.5">
                    {t('plan.card.script')}
                  </div>
                  <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
                    {plan.concept_explanation}
                  </p>
                </div>
              )}

              {plan.style_explanation && (
                <div>
                  <div className="text-sm font-semibold text-text-primary mb-1.5">
                    {t('plan.card.style')}
                  </div>
                  <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
                    {plan.style_explanation}
                  </p>
                </div>
              )}
            </div>
          </div>

          {(durationSec || aspectLabel || plan.language || credits !== undefined) && (
            <div>
              <div className="text-sm font-semibold text-text-primary mb-2">
                {t('plan.card.details')}
              </div>
              <div className="flex flex-wrap gap-2">
                {durationSec && (
                  <Chip icon={Clock}>{durationSec}s</Chip>
                )}
                {aspectLabel && <Chip icon={AspectIcon}>{aspectLabel}</Chip>}
                {plan.language && <Chip icon={Globe2}>{plan.language}</Chip>}
                <Chip icon={CaptionsIcon}>
                  {plan.cc_enabled !== false
                    ? t('plan.card.captionsEnabled')
                    : t('plan.card.captionsDisabled')}
                </Chip>
                {credits !== undefined && (
                  <Chip icon={Coins} tooltip={t('plan.card.creditsTooltip')}>
                    {t('plan.card.creditsValue', { count: credits })}
                  </Chip>
                )}
              </div>
            </div>
          )}
        </div>

        {scenes.length > 0 && (
          <div className="border-t border-glass-border">
            <div className="hidden md:grid grid-cols-[170px_1fr_1fr] gap-4 px-5 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wide">
              <div>{t('plan.card.colScene')}</div>
              <div>{t('plan.card.colVoiceover')}</div>
              <div>{t('plan.card.colVisual')}</div>
            </div>
            {scenes.map((scene, idx) => {
              const range = sceneTimeRange(scene);
              const rollLabel = scene.type === 'a_roll' || scene.type === 'b_roll'
                ? t(`plan.card.rolls.${scene.type}` as const)
                : scene.type;
              return (
                <div
                  key={`scene-${idx}`}
                  className="md:grid md:grid-cols-[170px_1fr_1fr] gap-4 px-5 py-4 border-t border-glass-border odd:bg-transparent even:bg-surface-light/25"
                >
                  <div className="mb-2 md:mb-0">
                    <div className="text-sm font-semibold text-text-primary leading-tight">
                      {scene.title || t('plan.card.sceneNumber', { index: idx + 1 })}
                    </div>
                    {range && (
                      <div className="text-xs text-text-tertiary mt-0.5">{range}</div>
                    )}
                    {rollLabel && (
                      <div className="text-xs text-text-tertiary">{rollLabel}</div>
                    )}
                  </div>
                  <div className="mb-2 md:mb-0">
                    <div className="md:hidden text-[10px] uppercase tracking-wide font-semibold text-text-tertiary mb-1">
                      {t('plan.card.colVoiceover')}
                    </div>
                    {scene.vo_script ? (
                      <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
                        {scene.vo_script}
                      </p>
                    ) : (
                      <p className="text-xs text-text-tertiary italic">
                        {t('plan.card.noVoiceover')}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="md:hidden text-[10px] uppercase tracking-wide font-semibold text-text-tertiary mb-1">
                      {t('plan.card.colVisual')}
                    </div>
                    {scene.visual_explanation ? (
                      <VisualExplanation
                        text={scene.visual_explanation}
                        userId={userId}
                        orderId={orderId}
                        cache={resourceCache}
                      />
                    ) : (
                      <p className="text-xs text-text-tertiary italic">
                        {t('plan.card.noVisual')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(onApprove || onRequestChanges) && (
          <div className="px-5 py-4 border-t border-glass-border bg-primary/5 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              {onApprove && (
                insufficientCredits ? (
                  <Button
                    onClick={handleGetCreditsClick}
                    disabled={!!isApproving || !!isRevising}
                    size="md"
                    className="flex-1"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    {t('plan.card.getCreditsButton')}
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerateClick}
                    disabled={
                      !!disabled
                      || !!isApproving
                      || !!isRevising
                      || !durationSec
                    }
                    size="md"
                    className="flex-1"
                    aria-label={t('plan.modal.approveAriaLabel')}
                  >
                    {isApproving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {t('plan.card.generateButton')}
                    {credits !== undefined && (
                      <span className="ml-2 text-xs opacity-80">
                        {t('plan.card.generateCredits', { count: credits })}
                      </span>
                    )}
                  </Button>
                )
              )}
              {onRequestChanges && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowRevise((v) => !v)}
                  disabled={!!disabled || !!isApproving || !!isRevising}
                  className="flex-1 sm:flex-initial"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('plan.card.reviseButton')}
                </Button>
              )}
            </div>
            {typeof userCredits === 'number' && (
              <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
                <AlertCircle className="w-3 h-3" />
                <span>
                  {insufficientCredits
                    ? t('plan.card.insufficientCredits', {
                      need: credits,
                      have: userCredits,
                    })
                    : t('plan.card.creditsReminder', { credits: userCredits })}
                </span>
              </div>
            )}
            {showRevise && onRequestChanges && (
              <div className="pt-2 border-t border-glass-border/60 space-y-2">
                <Textarea
                  value={reviseText}
                  onChange={(e) => setReviseText(e.target.value)}
                  placeholder={t('plan.card.revisePlaceholder')}
                  className="min-h-[72px] max-h-[180px]"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowRevise(false);
                      setReviseText('');
                    }}
                    disabled={!!isRevising}
                  >
                    {t('plan.card.reviseCancel')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmitRevision}
                    disabled={!reviseText.trim() || !!isRevising}
                  >
                    {isRevising ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                    ) : null}
                    {t('plan.card.reviseSubmit')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
